import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useNavigate } from 'react-router-dom';

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';
import { setGroupPolygonColour, setDevicePointColour, setCameraDeviceIcon } from 'utils/map';
import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';
import { useSoftUIController } from 'hooks/context';


function setGroupFeatures(group, showAdvancedDeviceLevel) {
  const { geojson, ...metadata } = group;

  var group_features = geojson;

  group_features.forEach((feature) => {
    feature.properties = {
      ...metadata,
      access: showAdvancedDeviceLevel,
      feature_colour: setGroupPolygonColour(group, showAdvancedDeviceLevel)
    };
  });

  return group_features;
}

function setDeviceFeatures(device, showAdvancedDeviceLevel) {
  var ret = {
    type: "Feature",
    properties: {
      ...device,
      access: showAdvancedDeviceLevel,
      ...setDevicePointColour(device, showAdvancedDeviceLevel)
    },
    geometry: {
      type: 'Point',
      coordinates: device.position
    }
  }

  if (device.hardware_type == "bk-camera") {
    ret.properties.icon = setCameraDeviceIcon(device, showAdvancedDeviceLevel);
  }

  return ret;
}

function ClearwaysGroupsMap({ map, user, groups_data, setGroupPreviewID, showDevices }) {

  const navigate = useNavigate();

  const [controller] = useSoftUIController();
  const { userPermissions } = controller;

  const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  const handleGroupClick = (e) => {
    e.preventDefault();
    setGroupPreviewID(e.features[0].properties.group_id);
    navigate("/overview/" + e.features[0].properties.group_id);
  }

  const handleDeviceClick = (e) => {
    e.preventDefault();
    popup.remove();
    navigate("/devices/" + e.features[0].properties.uuid);
  }

  const handleGroupMouseEnter = (e) => {
    map.current.getCanvas().style.cursor = 'pointer';
  }

  const handleGroupMouseLeave = (e) => {
    map.current.getCanvas().style.cursor = '';
  }

  const handleDeviceMouseEnter = (e) => {
    map.current.getCanvas().style.cursor = 'pointer';

    const coordinates = e.features[0].geometry.coordinates.slice();
    var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
    if (showAdvancedDeviceLevel) {
      description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
    }

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
  }

  const handleDeviceMouseLeave = (e) => {
    map.current.getCanvas().style.cursor = '';
    popup.remove();
  }

  useEffect(() => {  //mount
    if (!map.current) return;

    if (!map.current.hasImage('cameraIconOnline')) {
      map.current.loadImage(cameraOnline, (error, image) => {
        if (error) throw error;
        if (!map.current.hasImage('cameraIconOnline')) {
          map.current.addImage('cameraIconOnline', image);
        }
      });
    }

    if (!map.current.hasImage('cameraIconWarning')) {
      map.current.loadImage(cameraWarning, (error, image) => {
        if (error) throw error;
        if (!map.current.hasImage('cameraIconWarning')) {
          map.current.addImage('cameraIconWarning', image);
        }
      });
    }

    if (!map.current.hasImage('cameraIconOffline')) {
      map.current.loadImage(cameraOffline, (error, image) => {
        if (error) throw error;
        if (!map.current.hasImage('cameraIconOffline')) {
          map.current.addImage('cameraIconOffline', image);
        }
      });
    }

    var existing_camera_device_layer = map.current.getLayer('device-img-layer');
    var existing_camera_device_source = map.current.getSource('device-img-source');

    var existing_device_source = map.current.getSource('devices-source');
    var existing_device_layer = map.current.getLayer('devices-layer');

    var existing_group_source = map.current.getSource('groups-source');
    var existing_group_layer = map.current.getLayer('groups-layer');

    if (!existing_camera_device_source) {
      map.current.addSource('device-img-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: groups_data.flatMap(group => group.devices).filter(device => device.hardware_type == "bk-camera").map(device => setDeviceFeatures(device, showAdvancedDeviceLevel)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
        }
      });
    }
    if (!existing_camera_device_layer) {
      map.current.addLayer({
        id: 'device-img-layer',
        type: 'symbol',
        source: 'device-img-source',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': 0.044,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }

    if (!existing_device_source) {
      map.current.addSource('devices-source', {
        type: 'geojson',
        data: {
          "type": "FeatureCollection",
          "features": groups_data.flatMap(group => group.devices).filter(device => device.hardware_type != "bk-camera").map(device => setDeviceFeatures(device, showAdvancedDeviceLevel)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
        }
      });
    }

    if (!existing_device_layer) {
      map.current.addLayer({
        id: 'devices-layer',
        type: 'circle',
        source: 'devices-source',
        paint: {
          'circle-color': ['get', 'feature_colour'],
          'circle-radius': 9,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });
    }

    const processedFeatures = groups_data.flatMap((group) => setGroupFeatures(group, showAdvancedDeviceLevel));

    if (!existing_group_source) {
      map.current.addSource('groups-source', {
        type: 'geojson',
        data: {
          "type": "FeatureCollection",
          "features": processedFeatures
        }
      });
    }
    else {
      existing_group_source.setData({
        "type": "FeatureCollection",
        "features": processedFeatures
      });
    }

    if (!existing_group_layer) {
      map.current.addLayer({
        id: 'groups-layer',
        type: 'fill',
        source: 'groups-source',
        paint: {
          'fill-color': ['get', 'feature_colour'],
          'fill-opacity': 0.2,
        }
      }).addLayer({
        id: 'groups-layer-liner',
        type: 'line',
        source: 'groups-source',
        paint: {
          "line-color": ['get', 'feature_colour'],
          "line-width": 2
        }
      });
    }

    map.current.on('click', 'device-img-layer', handleDeviceClick);
    map.current.on('mouseenter', 'device-img-layer', handleDeviceMouseEnter);
    map.current.on('mouseleave', 'device-img-layer', handleDeviceMouseLeave);

    map.current.on('click', 'devices-layer', handleDeviceClick);
    map.current.on('mouseenter', 'devices-layer', handleDeviceMouseEnter);
    map.current.on('mouseleave', 'devices-layer', handleDeviceMouseLeave);

    map.current.on('click', 'groups-layer', handleGroupClick);
    map.current.on('mouseenter', 'groups-layer', handleGroupMouseEnter);
    map.current.on('mouseleave', 'groups-layer', handleGroupMouseLeave);

    return () => {
      map.current.off('click', 'groups-layer', handleGroupClick);
      map.current.off('mouseenter', 'groups-layer', handleGroupMouseEnter);
      map.current.off('mouseleave', 'groups-layer', handleGroupMouseLeave);

      map.current.off('click', 'devices-layer', handleDeviceClick);
      map.current.off('mouseenter', 'devices-layer', handleDeviceMouseEnter);
      map.current.off('mouseleave', 'devices-layer', handleDeviceMouseLeave);

      map.current.off('click', 'device-img-layer', handleDeviceClick);
      map.current.off('mouseenter', 'device-img-layer', handleDeviceMouseEnter);
      map.current.off('mouseleave', 'device-img-layer', handleDeviceMouseLeave);
    }
  }, [showDevices, groups_data]);

  useEffect(() => {
    if (!map.current) return;

    map.current.setFilter('device-img-layer', showDevices ? ['all'] : ['==', ['id'], '']);
    map.current.setFilter('devices-layer', showDevices ? ['all'] : ['==', ['id'], '']);
    map.current.setFilter('groups-layer', !showDevices ? ['all'] : ['==', ['id'], '']);
    map.current.setFilter('groups-layer-liner', !showDevices ? ['all'] : ['==', ['id'], '']);

  }, [showDevices]);

  useEffect(() => () => { //unmount

    map.current.removeImage('cameraIconOnline');
    map.current.removeImage('cameraIconWarning');
    map.current.removeImage('cameraIconOffline');

    map.current.removeLayer('groups-layer-liner');
    map.current.removeLayer('groups-layer');
    map.current.removeSource('groups-source');

    map.current.removeLayer('devices-layer');
    map.current.removeSource('devices-source');

    map.current.removeLayer('device-img-layer');
    map.current.removeSource('device-img-source');

    map.current.getCanvas().style.cursor = '';
    popup.remove();
  }, []);
}

ClearwaysGroupsMap.propTypes = {
  map: PropTypes.object,
  user: PropTypes.object,
  groups_data: PropTypes.array,
  setGroupPreviewID: PropTypes.func,
  showDevices: PropTypes.bool,
};

export default ClearwaysGroupsMap;